// =============================================================================
// INDUSTRY CONFIGURATION
// Maps each industry to terminology, workflows, default data, and feature flags
// =============================================================================

export type Industry =
  | 'furniture_millwork'
  | 'general_contractor'
  | 'plumber'
  | 'electrician'
  | 'handyman'
  | 'real_estate'
  | 'flooring';

export interface IndustryTerminology {
  job: string;
  quote: string;
  customer: string;
  vendor: string;
  lineItem: string;
  technician: string;
}

export interface IndustryFeatures {
  fabricationOrders: boolean;
  millworkPartners: boolean;
  rushOrders: boolean;
  installationScheduling: boolean;
  permitTracking: boolean;
  materialOrders: boolean;
  // Vertical-specific configurator flags. Each industry has its own
  // direct-manipulation configurator backed by an SVG canvas + live BOM.
  // Keeping these as discrete booleans makes the gate self-documenting and
  // forward-compatible: new configurators are added as new flags, old ones
  // stay put. Default for any unset flag is `false`.
  bunkConfigurator: boolean;
  // 2.5D unfolded shower stall (back/left/right walls + floor). Place niches,
  // benches, drains, curbs. Lives in src/verticals/flooring/.
  showerConfigurator: boolean;
}

// =============================================================================
// MY NETWORK — per-vertical vernacular
//
// The "My Network" feature uses six generic relationship types
// (self / satellite / vendor / subcontractor / partner / prospect) but the
// vocabulary that describes them differs by trade. A flooring tenant's
// "subcontractor" is an "Install Crew." A plumber's "vendor" is a "Supply
// House." A millwork tenant's "partner" is an "Authorized Millwork Partner."
//
// Each vertical can:
//   • rename any of the 6 types to vernacular that fits the trade
//   • supply a one-line description that goes on encouragement cards
//   • mark a type `hidden: true` to omit it from the picker (e.g., plumbers
//     don't think in terms of "satellite locations")
//   • override the nav labels in the dashboard
//
// Anything not specified falls through to the generic copy in
// PartnerNetwork.tsx so the system stays forward-compatible — adding a new
// vertical only needs the entries that actually differ from the defaults.
//
// REAL-WORLD HIERARCHY NOTE: subs have sub-subs, GCs have subs, and so on.
// The schema (partners.parent_partner_id from migration 20260509) supports
// arbitrary depth. Each vertical's `subSubcontractorLabel` describes what
// the next level down is called in their world.
// =============================================================================

export interface NetworkRelationshipMeta {
  label: string;
  pluralLabel: string;
  description: string;
  hidden?: boolean;
}

// =============================================================================
// DOMINO SEQUENCE — the canonical job-flow order per vertical
//
// Every trade repeats the same sequence of phases on every job ("the dominoes
// stand up and fall down in the same order"). The bank captures this as a
// Schedule of Values (AIA G702/G703): a spreadsheet listing every line in the
// exact order each trade engages with the property.
//
// Encoding the sequence as data — not prose — turns the SOV from a thing
// each user re-types into Excel into a thing Realtimax can DRAFT for them
// the moment a job is opened, pre-populated with the partners they've
// curated for each phase.
//
// Each DominoPhase is one SOV line:
//   id: stable identifier (snake_case)
//   label: human display label in the trade's vernacular
//   description: short context for the operator + future SOV preview
//   defaultRelationshipType: which of the 6 buckets typically owns the line
//     (so the system knows which partner type to suggest when the operator
//     fills in "who's on this line?")
//   inspection: optional flag — true when this phase is an AHJ checkpoint,
//     not labor (e.g., "rough inspection," "final inspection"). The SOV
//     treats inspections as gates between paid lines, not paid lines.
//   suggestedScopeRoles: optional finer-grained roles inside this phase,
//     pulled from `additionalTypes`. Today this is documentation; tomorrow
//     it drives a sub-picker on the SOV line ("which kind of inspector?").
//
// SOURCING: every sequence below is grounded in trade publications surfaced
// during field research (Oatey on plumbing phases, East-West Electric on
// electrical stages, BLS on flooring crew structure, AWI on millwork shop
// flow, Stewart Title on real-estate transaction timeline, GC trade-
// sequencing guides for the kitchen/bath remodel canonical pattern).
// =============================================================================

export interface DominoPhase {
  id: string;
  label: string;
  description: string;
  defaultRelationshipType: 'self' | 'satellite' | 'vendor' | 'subcontractor' | 'partner' | 'prospect';
  inspection?: boolean;
  suggestedScopeRoles?: string[];
}

export interface IndustryNetwork {
  // Dashboard nav
  navMacroLabel: string;
  navTabLabel: string;
  // Header copy when the network is empty
  emptyHeadline: string;
  emptySubtitle: string;
  // Per-relationship-type vernacular overrides
  relationshipLabels: {
    self?: NetworkRelationshipMeta;
    satellite?: NetworkRelationshipMeta;
    vendor?: NetworkRelationshipMeta;
    subcontractor?: NetworkRelationshipMeta;
    partner?: NetworkRelationshipMeta;
    prospect?: NetworkRelationshipMeta;
  };
  // What this vertical calls a sub-of-a-sub when one is added under a
  // top-level subcontractor in the hierarchy. Examples:
  //   GC: "Sub-Subcontractor"
  //   Flooring: "Crew Helper"
  //   Plumber: "Apprentice"
  //   Millwork: "Specialty Sub"
  // Used as a hint label when adding a child under another partner.
  subSubcontractorLabel: string;
  // FIELD-RESEARCH ARTIFACT — relationships that come up often enough in this
  // vertical to deserve their own first-class type someday, but which today
  // we collapse into the 6 generic buckets. Not yet consumed by the UI; this
  // is structured documentation for the next PR that wants to add per-vertical
  // pickers (e.g., a "Lender" sub-type of vendor for real estate, or
  // "Architect" sub-type of partner for GCs). Sourced from per-vertical
  // research against AWI / BLS / IBEW / RESPA / trade pubs.
  additionalTypes?: string[];
  // DOMINO SEQUENCE — the canonical line ordering for this vertical's SOV.
  // The same dominoes fall in the same order on every job; this array is
  // that order. The forthcoming SOV layer drafts a per-job line list from
  // this template, swapping in the tenant's curated partners per phase.
  dominoSequence?: DominoPhase[];
}

export interface DefaultService {
  name: string;
  unit: string;
  defaultPrice: number;
}

export interface IndustryConfig {
  id: Industry;
  label: string;
  icon: string;
  description: string;
  terminology: IndustryTerminology;
  jobStatuses: string[];
  defaultLineItemCategories: string[];
  defaultServices: DefaultService[];
  features: IndustryFeatures;
  network: IndustryNetwork;
}

export const INDUSTRY_CONFIGS: Record<Industry, IndustryConfig> = {
  furniture_millwork: {
    id: 'furniture_millwork',
    label: 'Custom Furniture / Millwork',
    icon: '🪵',
    description: 'Custom furniture, cabinetry, and millwork fabrication',
    terminology: {
      job: 'Order',
      quote: 'Quote',
      customer: 'Client',
      vendor: 'Millwork Partner',
      lineItem: 'Component',
      technician: 'Craftsman',
    },
    jobStatuses: [
      'Draft',
      'Quote Sent',
      'Modified',
      'In Production',
      'Paused',
      'Completed',
      'Withdrawn',
    ],
    defaultLineItemCategories: [
      'Bed Frame',
      'Stairs',
      'Add-Ons',
      'Hardware',
      'Finish',
      'Delivery',
    ],
    defaultServices: [
      { name: 'Twin XL over Queen Bunk', unit: 'unit', defaultPrice: 3200 },
      { name: 'Full over Full Bunk', unit: 'unit', defaultPrice: 2900 },
      { name: 'Custom Stairs', unit: 'set', defaultPrice: 800 },
      { name: 'Trundle Drawer', unit: 'unit', defaultPrice: 450 },
      { name: 'Delivery & Installation', unit: 'job', defaultPrice: 350 },
    ],
    features: {
      fabricationOrders: true,
      millworkPartners: true,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: false,
      materialOrders: true,
      bunkConfigurator: true,
      showerConfigurator: false,
    },
    // Vernacular grounded in AWI glossary, AWI Trade/Corporate Partner programs,
    // MKE Cabinetry & Covenant Millwork "Trade Partner" pages, J&K Cabinetry
    // "Authorized Wholesalers." A shop says "Trade partner" before it says
    // "Authorized millwork partner" — the latter is verbose and SaaS-coded.
    // The five-hand sequence (drafter → CNC → bench → finisher → installer)
    // is the canonical reusable pattern across every piece.
    network: {
      navMacroLabel: 'Partner Network',
      navTabLabel: 'Trade Partners',
      emptyHeadline: 'Map your bench, your suppliers, and the dealers running your work.',
      emptySubtitle:
        'Drafter, CNC, bench, finisher, installer — the same five hands run every piece. Map them, plus the lumber yards and finish suppliers feeding your shop, and the regional shops licensed to build your designs.',
      relationshipLabels: {
        self: {
          label: 'Our shop',
          pluralLabel: 'Our shops',
          description: 'The shop floor where the work gets built — bench, CNC, finish room, showroom.',
        },
        satellite: {
          label: 'Second shop',
          pluralLabel: 'Second shops',
          description: 'A branch shop or showroom in another market.',
        },
        vendor: {
          label: 'Materials supplier',
          pluralLabel: 'Materials suppliers',
          description: 'Lumber yards, hardware suppliers, finishing distributors, veneer and panel suppliers.',
        },
        subcontractor: {
          label: 'Specialty sub',
          pluralLabel: 'Specialty subs',
          description: "Finishers, carvers, upholsterers, gilders, installers — the outwork you don't do at the bench.",
        },
        partner: {
          label: 'Trade partner',
          pluralLabel: 'Trade partners',
          description: 'Authorized dealers and shops running your designs in their territory, plus the designers and architects who spec you in.',
        },
        prospect: {
          label: 'Prospective dealer',
          pluralLabel: 'Prospective dealers',
          description: "Cities, shops, and designers you want in the network but haven't onboarded yet.",
        },
      },
      subSubcontractorLabel: 'Specialty sub',
      additionalTypes: ['designer', 'installer', 'freight'],
      // The five-hand sequence per AWI / WoodJobs / live shop talk: drafter,
      // CNC, bench, finisher, installer. Same per-piece every time.
      dominoSequence: [
        { id: 'design_consult',     label: 'Design consult',          description: 'Client/designer scope, dimensions, materials, finish.', defaultRelationshipType: 'partner', suggestedScopeRoles: ['designer'] },
        { id: 'shop_drawings',      label: 'Shop drawings',           description: 'Drafter produces CAD + cut lists from the design.', defaultRelationshipType: 'self' },
        { id: 'po_materials',       label: 'PO + materials',          description: 'Lumber yard, hardware, finishing supplies on the way to the bench.', defaultRelationshipType: 'vendor' },
        { id: 'cnc_cut',            label: 'CNC cut',                 description: 'Sheet goods + parts cut to drawing.', defaultRelationshipType: 'self' },
        { id: 'bench_assembly',     label: 'Bench assembly',          description: 'Cabinetmaker assembles the piece on the floor.', defaultRelationshipType: 'self' },
        { id: 'finishing',          label: 'Finishing',               description: 'Stain, clear, paint, distressing — the look.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['specialty_sub'] },
        { id: 'qc_crate',           label: 'QC + crate',              description: 'Final inspection, blanket-wrap, custom crate for freight.', defaultRelationshipType: 'self' },
        { id: 'freight_install',    label: 'Freight + install',       description: 'White-glove freight, on-site install, punch list.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['installer', 'freight'] },
      ],
    },
  },

  general_contractor: {
    id: 'general_contractor',
    label: 'General Contractor',
    icon: '🏗️',
    description: 'Residential and commercial construction projects',
    terminology: {
      job: 'Project',
      quote: 'Bid',
      customer: 'Client',
      vendor: 'Subcontractor',
      lineItem: 'Scope Item',
      technician: 'Foreman',
    },
    jobStatuses: [
      'Lead',
      'Bid Sent',
      'Bid Won',
      'Permit Pending',
      'In Progress',
      'Punch List',
      'Completed',
      'Lost',
    ],
    defaultLineItemCategories: [
      'Demo',
      'Framing',
      'Electrical',
      'Plumbing',
      'HVAC',
      'Drywall',
      'Paint',
      'Flooring',
      'Trim',
    ],
    defaultServices: [
      { name: 'Project Management', unit: 'month', defaultPrice: 2500 },
      { name: 'Framing Labor', unit: 'sqft', defaultPrice: 8 },
      { name: 'Drywall Hang & Finish', unit: 'sqft', defaultPrice: 4.5 },
      { name: 'Interior Paint', unit: 'sqft', defaultPrice: 3 },
      { name: 'Permit Expediting', unit: 'permit', defaultPrice: 500 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: true,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: true,
      materialOrders: true,
      bunkConfigurator: false,
      showerConfigurator: false,
    },
    // CONTRADICTS earlier draft: "Strategic partner" is SaaS-speak. GCs say
    // "architect" / "designer" / "engineer" / "developer" / "repeat client" —
    // the bucket needs a name closer to the work itself. "Design partner" is
    // the cleanest umbrella; full split is in `additionalTypes` for later.
    // Sub-vernacular: GCs say "Sub" or "Trade"; "Trade subcontractor" is
    // verbose for a chip label.
    network: {
      navMacroLabel: 'Trade Network',
      navTabLabel: 'Subs, Suppliers & Designers',
      emptyHeadline: 'Stand up your trade network the way you actually run jobs.',
      emptySubtitle:
        "Demo, framer, plumber, electrician, HVAC, drywaller, tile, paint, flooring, trim — the dominoes fall in the same order on every job. Map your subs, your suppliers, the architects and engineers who hand you work, and the inspectors you'll see on every permit.",
      relationshipLabels: {
        self: {
          label: 'Our office',
          pluralLabel: 'Our offices',
          description: 'Your primary office or yard.',
        },
        satellite: {
          label: 'Branch office',
          pluralLabel: 'Branch offices',
          description: 'Branch offices or yards in other markets.',
        },
        vendor: {
          label: 'Supplier',
          pluralLabel: 'Suppliers',
          description: 'Lumberyards, drywall houses, concrete plants, steel suppliers, equipment rental — your purchase-order partners.',
        },
        subcontractor: {
          label: 'Sub',
          pluralLabel: 'Subs',
          description: 'Plumbers, electricians, framers, drywallers, painters, roofers — the trades that finish your scope. Subs can have their own crews under them.',
        },
        partner: {
          label: 'Design partner',
          pluralLabel: 'Design partners',
          description: 'Architects, designers, engineers, and developers who hand you projects.',
        },
        prospect: {
          label: 'Prospect',
          pluralLabel: 'Prospects',
          description: "Subs, suppliers, or design partners you want in your network but haven't worked with yet.",
        },
      },
      subSubcontractorLabel: 'Sub-sub',
      additionalTypes: ['architect', 'engineer', 'permit_expediter', 'inspector', 'repeat_client'],
      // Canonical kitchen/bath remodel sequence per Frey Construction, King
      // Remodeling, Procore, Buildern. New construction adds excavation/
      // foundation/framing before this list and landscaping after; the
      // remodel pattern is the most-cited template for SOV drafts.
      dominoSequence: [
        { id: 'demo',                 label: 'Demo',                       description: 'Tear-out and haul-away of existing fixtures/finishes.', defaultRelationshipType: 'subcontractor' },
        { id: 'framing',              label: 'Framing / carpentry',        description: 'Layout changes, blocking, structural carpentry.', defaultRelationshipType: 'subcontractor' },
        { id: 'plumbing_rough',       label: 'Plumbing rough-in',          description: 'DWV + supply lines in walls/floor before drywall.', defaultRelationshipType: 'subcontractor' },
        { id: 'electrical_rough',     label: 'Electrical rough-in',        description: 'Boxes, cable, panel work before drywall.', defaultRelationshipType: 'subcontractor' },
        { id: 'hvac_rough',           label: 'HVAC rough-in',              description: 'Ducts, line sets, equipment placement.', defaultRelationshipType: 'subcontractor' },
        { id: 'rough_inspection',     label: 'Rough inspection',           description: 'AHJ checkpoint before walls close up.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
        { id: 'insulation',           label: 'Insulation',                 description: 'Batts, blown, spray foam — sealed before drywall.', defaultRelationshipType: 'subcontractor' },
        { id: 'drywall',              label: 'Drywall hang + finish',      description: 'Hang, tape, mud, sand to ready-for-paint.', defaultRelationshipType: 'subcontractor' },
        { id: 'tile',                 label: 'Tile',                       description: 'Shower, tub surround, floor tile, accents.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['tile_finisher', 'waterproofing_sub'] },
        { id: 'cabinet_install',      label: 'Cabinet install',            description: 'Set boxes, doors, drawers, hardware.', defaultRelationshipType: 'subcontractor' },
        { id: 'countertop_template',  label: 'Countertop template',        description: 'Field measure for stone/quartz fab.', defaultRelationshipType: 'subcontractor' },
        { id: 'countertop_install',   label: 'Countertop install',         description: 'Set + seam tops; sink/cooktop cutouts on-site.', defaultRelationshipType: 'subcontractor' },
        { id: 'paint',                label: 'Paint',                      description: 'Prime, cut, roll, trim — walls to ceiling.', defaultRelationshipType: 'subcontractor' },
        { id: 'flooring',             label: 'Flooring',                   description: 'LVP/wood/tile field install (after cabs in modern practice).', defaultRelationshipType: 'subcontractor' },
        { id: 'trim',                 label: 'Trim carpentry',             description: 'Base, casing, crown, doors hung.', defaultRelationshipType: 'subcontractor' },
        { id: 'plumbing_trim',        label: 'Plumbing trim-out',          description: 'Toilets, faucets, drains, supplies connected.', defaultRelationshipType: 'subcontractor' },
        { id: 'electrical_trim',      label: 'Electrical trim-out',        description: 'Devices, fixtures, appliance hookups.', defaultRelationshipType: 'subcontractor' },
        { id: 'final_clean',          label: 'Final clean',                description: 'Construction clean before walk-through.', defaultRelationshipType: 'subcontractor' },
        { id: 'final_inspection',     label: 'Final inspection',           description: 'AHJ certificate of occupancy / final sign-off.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
        { id: 'punch_list',           label: 'Punch list',                 description: 'Walk-through items closed out before retainage release.', defaultRelationshipType: 'self' },
      ],
    },
  },

  plumber: {
    id: 'plumber',
    label: 'Plumber',
    icon: '🔧',
    description: 'Residential and commercial plumbing services',
    terminology: {
      job: 'Service Call',
      quote: 'Estimate',
      customer: 'Homeowner',
      vendor: 'Supplier',
      lineItem: 'Service',
      technician: 'Plumber',
    },
    jobStatuses: [
      'New Request',
      'Estimate Sent',
      'Approved',
      'Scheduled',
      'In Progress',
      'Completed',
      'Cancelled',
    ],
    defaultLineItemCategories: [
      'Diagnostic',
      'Labor',
      'Parts',
      'Fixtures',
      'Emergency Fee',
    ],
    defaultServices: [
      { name: 'Service Call / Diagnostic', unit: 'visit', defaultPrice: 125 },
      { name: 'Drain Cleaning', unit: 'drain', defaultPrice: 185 },
      { name: 'Water Heater Installation', unit: 'unit', defaultPrice: 850 },
      { name: 'Toilet Replacement', unit: 'unit', defaultPrice: 350 },
      { name: 'Emergency After-Hours Fee', unit: 'flat', defaultPrice: 250 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: false,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: true,
      materialOrders: true,
      bunkConfigurator: false,
      showerConfigurator: false,
    },
    // CONTRADICTS earlier draft: "Helper / Apprentice" labels W-2 crew as if
    // they're 1099 subs. The actual sub case for plumbers is sewer-cam/jetter,
    // hydro-excavation, septic, gas-fitter, backflow tester. Combining the
    // label as "Helper / Sub" lets the type cover both, and the hierarchy
    // (parent_partner_id) handles "apprentice under journeyman."
    // Domino sequence per Oatey / Plumbing Concepts: rough → top-out → trim.
    network: {
      navMacroLabel: 'My Network',
      navTabLabel: 'Supply Houses, GCs & Crew',
      emptyHeadline: 'Map your supply house, your GCs, and the crew on your truck.',
      emptySubtitle:
        'Same supply house, same GCs, same apprentices, same showroom for every fixture spec. Make the pattern visible so the next service call or rough-in stands itself up.',
      relationshipLabels: {
        self: {
          label: 'Our shop',
          pluralLabel: 'Our shop',
          description: 'Your shop or your truck.',
        },
        satellite: {
          label: 'Branch',
          pluralLabel: 'Branches',
          description: 'A second yard or service area you operate from.',
          hidden: true,
        },
        vendor: {
          label: 'Supply house',
          pluralLabel: 'Supply houses',
          description: 'Ferguson, Hajoca, Winsupply, your local — where you pick up fittings, fixtures, and emergency parts.',
        },
        subcontractor: {
          label: 'Helper / Sub',
          pluralLabel: 'Helpers & subs',
          description: 'Apprentices and journeymen on your crew, plus specialty subs you bring on for sewer-cam, jetting, gas, or septic.',
        },
        partner: {
          label: 'GC / Builder / PM',
          pluralLabel: 'GCs, builders & PMs',
          description: 'General contractors, home builders, and property managers who call you first when they need a plumber.',
        },
        prospect: {
          label: 'Prospect GC',
          pluralLabel: 'Prospect GCs',
          description: 'GCs, builders, or property managers you want as repeat clients.',
        },
      },
      subSubcontractorLabel: 'Apprentice',
      additionalTypes: ['showroom', 'property_manager', 'inspector'],
      // The three classic phases of new-construction plumbing per Oatey and
      // Plumbing Concepts. Service-call work uses a different short loop
      // (diagnose → access → repair → test → restore) handled at the job
      // record level, not the SOV.
      dominoSequence: [
        { id: 'underground_rough',    label: 'Underground rough-in',  description: 'DWV + supply under slab, pressure tested before pour.', defaultRelationshipType: 'self' },
        { id: 'underground_inspect',  label: 'Underground inspection',description: 'AHJ inspects before slab pour covers the work.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
        { id: 'top_out',              label: 'Top-out (above-ground rough)', description: 'In-wall + ceiling rough; pressure tested before drywall.', defaultRelationshipType: 'self' },
        { id: 'top_out_inspect',      label: 'Top-out inspection',    description: 'AHJ inspects before walls close up.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
        { id: 'fixture_pickup',       label: 'Fixture pickup',        description: 'Showroom-spec fixtures collected from supply house for trim.', defaultRelationshipType: 'vendor', suggestedScopeRoles: ['showroom'] },
        { id: 'trim_out',             label: 'Trim-out',              description: 'Set fixtures, faucets, toilets, appliance connections.', defaultRelationshipType: 'self' },
        { id: 'final_inspect',        label: 'Final inspection',      description: 'AHJ final + leak check before sign-off.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
      ],
    },
  },

  electrician: {
    id: 'electrician',
    label: 'Electrician',
    icon: '⚡',
    description: 'Residential and commercial electrical services',
    terminology: {
      job: 'Work Order',
      quote: 'Estimate',
      customer: 'Property Owner',
      vendor: 'Supplier',
      lineItem: 'Service',
      technician: 'Electrician',
    },
    jobStatuses: [
      'New Request',
      'Estimate Sent',
      'Approved',
      'Permit Pulled',
      'Scheduled',
      'In Progress',
      'Inspection',
      'Completed',
      'Cancelled',
    ],
    defaultLineItemCategories: [
      'Diagnostic',
      'Labor',
      'Materials',
      'Panel Work',
      'Permit',
      'Inspection',
    ],
    defaultServices: [
      { name: 'Service Call / Diagnostic', unit: 'visit', defaultPrice: 150 },
      { name: 'Outlet Installation', unit: 'outlet', defaultPrice: 175 },
      { name: 'Panel Upgrade 200A', unit: 'unit', defaultPrice: 2800 },
      { name: 'Circuit Installation', unit: 'circuit', defaultPrice: 350 },
      { name: 'EV Charger Installation', unit: 'unit', defaultPrice: 1200 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: false,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: true,
      materialOrders: true,
      bunkConfigurator: false,
      showerConfigurator: false,
    },
    // Same Helper/Sub treatment as plumber — same category mistake to avoid.
    // Specialty subs in electrical: low-voltage (data/AV/security/fire),
    // solar/EV/battery, boring/trenching subs. POCO (power company) and
    // AHJ (inspector) are off-chain touchpoints worth surfacing as
    // additionalTypes. Domino sequence per East-West Electric / Fine
    // Homebuilding: temp → rough → trim → final.
    network: {
      navMacroLabel: 'My Network',
      navTabLabel: 'Supply Houses, GCs & Crew',
      emptyHeadline: 'Map your supply house, your GCs, and the crew on your truck.',
      emptySubtitle:
        'Temp power, rough, trim, final — the same dance every job. Same supply house, same GCs, same apprentices. Map the pattern so the next bid pre-fills itself.',
      relationshipLabels: {
        self: {
          label: 'Our shop',
          pluralLabel: 'Our shop',
          description: 'Your shop or your service truck.',
        },
        satellite: {
          label: 'Branch',
          pluralLabel: 'Branches',
          description: 'A second shop or service area you operate from.',
          hidden: true,
        },
        vendor: {
          label: 'Supply house',
          pluralLabel: 'Supply houses',
          description: 'Wesco, Graybar, Rexel, CED, your local — wire, breakers, panels, fixtures, gear.',
        },
        subcontractor: {
          label: 'Helper / Sub',
          pluralLabel: 'Helpers & subs',
          description: 'Apprentices and journeymen on your crew, plus low-voltage, solar, and boring subs you bring on for specialty scope.',
        },
        partner: {
          label: 'GC / Builder / FM',
          pluralLabel: 'GCs, builders & facility managers',
          description: 'General contractors, builders, property and facility managers who pull you in on every project.',
        },
        prospect: {
          label: 'Prospect GC',
          pluralLabel: 'Prospect GCs',
          description: 'GCs, builders, or facility managers you want as repeat clients.',
        },
      },
      subSubcontractorLabel: 'Apprentice',
      additionalTypes: ['inspector', 'utility', 'low_voltage_sub'],
      // Five stages of residential electrical construction per East-West
      // Electric and Fine Homebuilding's "Rough-In Basics." Remodel skips
      // temp_power and adds wire-fishing between rough and trim — the SOV
      // omits temp on remodels.
      dominoSequence: [
        { id: 'temp_power',           label: 'Temporary power',       description: 'Temp pole/panel for the jobsite during construction.', defaultRelationshipType: 'self' },
        { id: 'service_entrance',     label: 'Service entrance',      description: 'Underground or aerial service drop; meter base set.', defaultRelationshipType: 'self', suggestedScopeRoles: ['utility'] },
        { id: 'rough_in',             label: 'Rough-in',              description: 'Boxes, drilling, cable pulls, panel set — before insulation.', defaultRelationshipType: 'self' },
        { id: 'rough_inspection',     label: 'Rough inspection',      description: 'AHJ checkpoint before drywall covers the work.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector'] },
        { id: 'trim_out',             label: 'Trim-out',              description: 'Devices, fixtures, appliance hookups after paint + flooring.', defaultRelationshipType: 'self' },
        { id: 'low_voltage',          label: 'Low-voltage',           description: 'Data, AV, security, fire — separate license, often a sub.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['low_voltage_sub'] },
        { id: 'final_inspection',     label: 'Final inspection + meter', description: 'AHJ final, then utility connects the meter.', defaultRelationshipType: 'partner', inspection: true, suggestedScopeRoles: ['inspector', 'utility'] },
      ],
    },
  },

  handyman: {
    id: 'handyman',
    label: 'Handyman',
    icon: '🔨',
    description: 'General home repair and maintenance services',
    terminology: {
      job: 'Job',
      quote: 'Quote',
      customer: 'Homeowner',
      vendor: 'Supplier',
      lineItem: 'Task',
      technician: 'Handyman',
    },
    jobStatuses: [
      'New Request',
      'Quote Sent',
      'Approved',
      'Scheduled',
      'In Progress',
      'Completed',
      'Cancelled',
    ],
    defaultLineItemCategories: [
      'Labor',
      'Materials',
      'Hauling / Disposal',
      'Travel Fee',
    ],
    defaultServices: [
      { name: 'Hourly Labor', unit: 'hour', defaultPrice: 85 },
      { name: 'Half-Day Rate (4hrs)', unit: 'flat', defaultPrice: 300 },
      { name: 'Full-Day Rate (8hrs)', unit: 'flat', defaultPrice: 550 },
      { name: 'Materials Markup', unit: 'pct', defaultPrice: 20 },
      { name: 'Travel Fee', unit: 'flat', defaultPrice: 45 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: false,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: false,
      materialOrders: false,
      bunkConfigurator: false,
      showerConfigurator: false,
    },
    // CONTRADICTS earlier draft: previous version repurposed the `satellite`
    // type as "Helper", which is a category error — helpers aren't locations.
    // The correct fix is to hide satellite (most handymen don't have one) and
    // fold helpers into `subcontractor` along with specialty referrals to
    // licensed trades. Handymen don't really *subcontract*; they hire helpers
    // (W-2 day labor) or refer (to a licensed plumber/EC when scope exceeds
    // their license). Both belong in the same bucket vocabulary-wise.
    network: {
      navMacroLabel: 'My Network',
      navTabLabel: 'Trades & Referrals',
      emptyHeadline: 'Map the realtors, PMs, and trades that keep your phone ringing.',
      emptySubtitle:
        "A handyman's network is the realtor who calls every Tuesday, the PM with twenty units, the licensed electrician you hand off to when it's out of scope, and the hardware store that knows your name. Map them so you don't lose a number.",
      relationshipLabels: {
        self: {
          label: 'My truck',
          pluralLabel: 'My truck',
          description: 'You and your truck. Add a shop or garage if you have one.',
        },
        satellite: {
          label: 'Second location',
          pluralLabel: 'Second locations',
          description: "A second truck or workshop. Hidden by default — most handymen don't have one.",
          hidden: true,
        },
        vendor: {
          label: 'Hardware store',
          pluralLabel: 'Hardware stores',
          description: "Home Depot, Lowe's, Ace, your local — and the pro desk relationships that get you priced in.",
        },
        subcontractor: {
          label: 'Helper / Specialty referral',
          pluralLabel: 'Helpers & referrals',
          description: 'The helper who rides along, plus the licensed plumber, electrician, or HVAC pro you hand the client to when scope exceeds your license.',
        },
        partner: {
          label: 'Realtor / PM',
          pluralLabel: 'Realtors & PMs',
          description: 'Realtors, property managers, HOAs, insurance adjusters, and home-warranty companies who throw repeat work your way.',
        },
        prospect: {
          label: 'Future client',
          pluralLabel: 'Future clients',
          description: 'New properties, PMs, or realtors you want to start working with.',
        },
      },
      subSubcontractorLabel: 'Helper',
      additionalTypes: ['helper', 'home_warranty_company', 'referral_group'],
      // Handyman jobs don't accumulate into bank-style draws — the SOV
      // pattern here is per-visit, not per-project. The sequence below is
      // the single-visit shape that repeats across every job; the across-
      // jobs pattern (Tuesday/Thursday appointments + Friday list-day) is
      // a scheduling concern, not an SOV.
      dominoSequence: [
        { id: 'phone_triage',         label: 'Phone triage',          description: 'Quick scope + sight-unseen quote for small items.', defaultRelationshipType: 'self' },
        { id: 'arrival_quote',        label: 'Arrival + on-site quote', description: 'Confirm scope, walk the work, set price.', defaultRelationshipType: 'self' },
        { id: 'material_run',         label: 'Material run',          description: 'Pickup at hardware store on the way to the job.', defaultRelationshipType: 'vendor' },
        { id: 'execute',              label: 'Execute',               description: 'Run the task list — usually a few discrete jobs in one visit.', defaultRelationshipType: 'self' },
        { id: 'specialty_handoff',    label: 'Specialty handoff',     description: 'Out-of-scope items handed to a licensed plumber/EC/HVAC pro.', defaultRelationshipType: 'subcontractor' },
        { id: 'cleanup_haul',         label: 'Cleanup + haul',        description: 'Site clean, debris haul-away.', defaultRelationshipType: 'self' },
        { id: 'photo_invoice',        label: 'Photo + invoice',       description: 'Before/after photos, invoice on the spot.', defaultRelationshipType: 'self' },
      ],
    },
  },

  real_estate: {
    id: 'real_estate',
    label: 'Real Estate',
    icon: '🏠',
    description: 'Real estate sales, property management, and related services',
    terminology: {
      job: 'Project',
      quote: 'Proposal',
      customer: 'Client',
      vendor: 'Partner',
      lineItem: 'Service',
      technician: 'Professional',
    },
    jobStatuses: [
      'Lead',
      'Proposal Sent',
      'Under Contract',
      'In Progress',
      'Inspection',
      'Closing',
      'Completed',
      'Lost',
    ],
    defaultLineItemCategories: [
      'Commission',
      'Management Fee',
      'Maintenance',
      'Inspection',
      'Renovation',
      'Staging',
    ],
    defaultServices: [
      { name: 'Property Listing', unit: 'listing', defaultPrice: 0 },
      { name: 'Property Management', unit: 'month', defaultPrice: 250 },
      { name: 'Home Inspection', unit: 'inspection', defaultPrice: 450 },
      { name: 'Maintenance Request', unit: 'job', defaultPrice: 150 },
      { name: 'Renovation Estimate', unit: 'project', defaultPrice: 0 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: false,
      rushOrders: false,
      installationScheduling: true,
      permitTracking: true,
      materialOrders: false,
      bunkConfigurator: false,
      showerConfigurator: false,
    },
    // CONTRADICTS earlier draft: the canonical industry term per RESPA / TREC
    // / brokerage materials is "Preferred vendor" — that's the noun on every
    // listing agent's referral sheet, not "Service provider."
    //
    // CAVEAT: real estate is the vertical where the 6-bucket model fits worst.
    // An agent's network breaks down by *role* (lender / title / inspector /
    // appraiser / photographer / stager / contractor / closing attorney / PM)
    // far more naturally than by self/satellite/vendor/sub/partner/prospect.
    // The `additionalTypes` list below documents the categories agents
    // actually use; a follow-up PR could add a `vendorCategory` enum on
    // `partners` rows for this vertical, or replace the type model entirely
    // for real estate. For now the existing buckets work as a coarse fit.
    //
    // Domino sequence per Stewart Title / South Oak Title: the dominoes that
    // fall on every transaction are inspector → appraiser → title → close.
    network: {
      navMacroLabel: 'My Network',
      navTabLabel: 'Preferred Vendors',
      emptyHeadline: 'Build the preferred vendor list you hand every client on day one.',
      emptySubtitle:
        'A great agent has a short list — the lender who answers, the inspector who shows up, the title officer who closes on time, the contractor who fixes what the inspection turns up. Map them here.',
      relationshipLabels: {
        self: {
          label: 'My office',
          pluralLabel: 'My office',
          description: 'Your primary brokerage office.',
        },
        satellite: {
          label: 'Branch office',
          pluralLabel: 'Branch offices',
          description: 'Other offices in your brokerage footprint.',
        },
        vendor: {
          label: 'Preferred vendor',
          pluralLabel: 'Preferred vendors',
          description: 'Lenders, title, inspectors, appraisers, photographers, stagers — the list you hand every client.',
        },
        subcontractor: {
          // Hidden in real estate: agents refer, they don't subcontract.
          label: 'Sub-trade',
          pluralLabel: 'Sub-trades',
          description: "Hidden — agents refer, they don't subcontract.",
          hidden: true,
        },
        partner: {
          label: 'Trusted contractor',
          pluralLabel: 'Trusted contractors',
          description: 'Renovation, plumbing, electrical, and roofing pros for pre-list repairs and post-inspection fixes.',
        },
        prospect: {
          label: 'Vendor to vet',
          pluralLabel: 'Vendors to vet',
          description: "Pros you've heard good things about but haven't put on the list yet.",
        },
      },
      subSubcontractorLabel: 'Sub-trade',
      additionalTypes: [
        'lender',
        'title_escrow',
        'inspector',
        'appraiser',
        'photographer',
        'stager',
        'closing_attorney',
        'property_manager',
      ],
      // Listing-side timeline per Stewart Title / South Oak Title's transaction
      // close guides. Buy-side starts at pre-approval (lender) and converges
      // at inspector/appraiser/title/close. The "SOV" for real estate isn't
      // a draw schedule — it's the close timeline, with vendor lines for
      // each touchpoint and dollar amounts for commission/fees/concessions.
      dominoSequence: [
        { id: 'listing_appointment',  label: 'Listing appointment',   description: 'CMA, pricing, listing agreement signed.', defaultRelationshipType: 'self' },
        { id: 'pre_list_inspection',  label: 'Pre-list inspection',   description: 'Optional — surfaces issues before buyer finds them.', defaultRelationshipType: 'vendor', suggestedScopeRoles: ['inspector'] },
        { id: 'staging',              label: 'Staging',               description: 'Furniture, art, accessories for showings.', defaultRelationshipType: 'vendor', suggestedScopeRoles: ['stager'] },
        { id: 'photography',          label: 'Photography',           description: 'Stills + video + 3D tour for MLS.', defaultRelationshipType: 'vendor', suggestedScopeRoles: ['photographer'] },
        { id: 'mls_list',             label: 'MLS list',              description: 'Active in MLS, syndicated to portals.', defaultRelationshipType: 'self' },
        { id: 'showings',             label: 'Showings',              description: 'Buyer-side agents tour the property.', defaultRelationshipType: 'self' },
        { id: 'offer_review',         label: 'Offer review',          description: 'Seller reviews and accepts/counters offers.', defaultRelationshipType: 'self' },
        { id: 'under_contract',       label: 'Under contract',        description: 'Earnest money + contingency clock starts.', defaultRelationshipType: 'self' },
        { id: 'buyer_inspection',     label: 'Buyer inspection',      description: 'Buyer-ordered inspection; report drives repair negotiation.', defaultRelationshipType: 'partner', suggestedScopeRoles: ['inspector'] },
        { id: 'repair_negotiation',   label: 'Repair negotiation',    description: 'Concessions, repairs, or price reduction in response to inspection.', defaultRelationshipType: 'self' },
        { id: 'repair_vendors',       label: 'Repair vendors',        description: 'Contractor/handyman closes inspection items.', defaultRelationshipType: 'partner' },
        { id: 'appraisal',            label: 'Appraisal',             description: 'Lender-ordered; agent tracks acceptable comps.', defaultRelationshipType: 'partner', suggestedScopeRoles: ['appraiser'] },
        { id: 'title_work',           label: 'Title work',            description: 'Title search, insurance, escrow setup.', defaultRelationshipType: 'partner', suggestedScopeRoles: ['title_escrow', 'closing_attorney'] },
        { id: 'final_walkthrough',    label: 'Final walkthrough',     description: 'Buyer confirms property condition before close.', defaultRelationshipType: 'self' },
        { id: 'close',                label: 'Close',                 description: 'Funds wired, deed recorded, keys exchanged.', defaultRelationshipType: 'partner', suggestedScopeRoles: ['title_escrow', 'lender'] },
        { id: 'possession',           label: 'Possession',            description: 'Buyer takes possession per contract terms.', defaultRelationshipType: 'self' },
      ],
    },
  },

  flooring: {
    id: 'flooring',
    label: 'Flooring',
    icon: '🪟',
    description: 'Residential and commercial flooring, tile, and shower installations',
    terminology: {
      job: 'Job',
      quote: 'Estimate',
      customer: 'Client',
      vendor: 'Supplier',
      lineItem: 'Material / Service',
      technician: 'Installer',
    },
    jobStatuses: [
      'Lead',
      'Estimate Sent',
      'Approved',
      'Scheduled',
      'Materials Ordered',
      'In Progress',
      'Punch List',
      'Completed',
      'Cancelled',
    ],
    defaultLineItemCategories: [
      'Demo / Removal',
      'Subfloor / Substrate',
      'Underlayment',
      'Waterproofing',
      'Material',
      'Trim / Transition',
      'Labor',
      'Disposal',
      'Delivery',
    ],
    defaultServices: [
      // Residential
      { name: 'LVP Install (Residential)', unit: 'sqft', defaultPrice: 4.5 },
      { name: 'Tile Install — Floor (Residential)', unit: 'sqft', defaultPrice: 9 },
      { name: 'Tile Install — Shower Walls to Ceiling', unit: 'sqft', defaultPrice: 14 },
      { name: 'Carpet Install (Residential)', unit: 'sqft', defaultPrice: 3.5 },
      { name: 'Hardwood Refinish', unit: 'sqft', defaultPrice: 5 },
      // Commercial
      { name: 'LVT / VCT Install (Commercial)', unit: 'sqft', defaultPrice: 5.5 },
      { name: 'Polished Concrete (Commercial)', unit: 'sqft', defaultPrice: 7 },
      { name: 'Carpet Tile (Commercial)', unit: 'sqft', defaultPrice: 4 },
      // Common
      { name: 'Tear-Out / Demo', unit: 'sqft', defaultPrice: 1.5 },
      { name: 'Subfloor Repair', unit: 'sqft', defaultPrice: 6 },
      { name: 'Trim / Transition Strip', unit: 'lf', defaultPrice: 8 },
      { name: 'Site Visit / Estimate', unit: 'visit', defaultPrice: 0 },
    ],
    features: {
      fabricationOrders: false,
      millworkPartners: false,
      rushOrders: true,
      installationScheduling: true,
      permitTracking: false,
      materialOrders: true,
      bunkConfigurator: false,
      showerConfigurator: true,
    },
    // Vendor noun is "Distributor" (commercial) or "Wholesaler" (residential
    // shops). Daltile, MSI, Mohawk, Shaw, Engineered Floors are the names
    // every flooring contractor drops. Sub-vernacular: install crews are 1099
    // in residential, W-2 in commercial union shops — same word for both.
    // The four-act recurring pattern (demo → prep → install → finish) is the
    // flooring trade's signature domino sequence and is already surfaced in
    // the empty-state subtitle.
    network: {
      navMacroLabel: 'My Network',
      navTabLabel: 'Crews, Distributors & GCs',
      emptyHeadline: 'Map your install crews, your distributors, and the GCs who keep calling.',
      emptySubtitle:
        'Demo → prep → install → finish. The same four-act sequence runs every job. Map the crews and helpers who run it with you, the distributors your POs go to, and the GCs and designers who hand you the next one.',
      relationshipLabels: {
        self: {
          label: 'Our showroom',
          pluralLabel: 'Our showroom',
          description: 'Your primary showroom, shop, or warehouse.',
        },
        satellite: {
          label: 'Branch showroom',
          pluralLabel: 'Branch showrooms',
          description: 'Branch showrooms or yards in other markets.',
        },
        vendor: {
          label: 'Distributor',
          pluralLabel: 'Distributors',
          description: 'Tile distributors, LVP wholesalers, hardwood mills — Daltile, MSI, Mohawk, Shaw, your local — your purchase-order partners.',
        },
        subcontractor: {
          label: 'Install crew',
          pluralLabel: 'Install crews',
          description: 'The crews running the demo → prep → install → finish sequence on your jobs. Each crew can have its own helpers and a tile finisher under it.',
        },
        partner: {
          label: 'GC / Designer',
          pluralLabel: 'GCs & designers',
          description: 'General contractors, designers, and spec firms who pull you onto their projects.',
        },
        prospect: {
          label: 'Prospect crew or GC',
          pluralLabel: 'Prospect crews & GCs',
          description: "Crews and GCs you want in the network but haven't worked with yet.",
        },
      },
      subSubcontractorLabel: 'Crew helper',
      additionalTypes: ['demo_crew', 'waterproofing_sub', 'tile_finisher', 'mill_direct'],
      // Tile shower (the canonical complex flooring job) per Tile Shop and
      // Flexstone install guides. Field flooring (LVP/wood) is a subset:
      // demo → subfloor prep → underlayment → install → trim. The four-act
      // pattern (demo → prep → install → finish) holds across both.
      dominoSequence: [
        { id: 'demo',                 label: 'Demo',                  description: 'Rip out existing tile/flooring + drywall behind it; haul off.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['demo_crew'] },
        { id: 'subfloor_prep',        label: 'Subfloor prep',         description: 'Sister studs, level checks, install backer board / underlayment.', defaultRelationshipType: 'subcontractor' },
        { id: 'waterproofing',        label: 'Waterproofing',         description: 'Membrane on walls/floor/seams + pipe penetrations (showers).', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['waterproofing_sub'] },
        { id: 'layout',               label: 'Layout',                description: 'Chalk lines, plan field tile + niches + accents before set.', defaultRelationshipType: 'subcontractor' },
        { id: 'install',              label: 'Install',               description: 'Set tile or rack/click LVP/hardwood — the bulk of the labor.', defaultRelationshipType: 'subcontractor' },
        { id: 'cure',                 label: 'Cure',                  description: 'Thin-set cure (24-48hr) before grout — gate, not labor.', defaultRelationshipType: 'self' },
        { id: 'finish',               label: 'Finish',                description: 'Grout, sand-and-seal, or finish coat depending on material.', defaultRelationshipType: 'subcontractor', suggestedScopeRoles: ['tile_finisher'] },
        { id: 'silicone',             label: 'Silicone + caulk',      description: 'Change-of-plane corners, tub/shower joints.', defaultRelationshipType: 'subcontractor' },
        { id: 'trim_transition',      label: 'Trim + transition',     description: 'Transition strips, baseboard, shoe molding.', defaultRelationshipType: 'subcontractor' },
        { id: 'final_clean_seal',     label: 'Final clean + seal',    description: 'Grout haze, final wipe, optional stone/grout sealer.', defaultRelationshipType: 'self' },
      ],
    },
  },
};

// Status badge colors — indexed by position in jobStatuses array
export const STATUS_COLORS = [
  'bg-gray-100 text-gray-700',
  'bg-blue-100 text-blue-700',
  'bg-yellow-100 text-yellow-700',
  'bg-orange-100 text-orange-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-red-100 text-red-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
];

// Default terminology fallback (used when no tenant is loaded)
export const DEFAULT_TERMINOLOGY: IndustryTerminology = {
  job: 'Job',
  quote: 'Quote',
  customer: 'Customer',
  vendor: 'Vendor',
  lineItem: 'Line Item',
  technician: 'Tech',
};
